import { NgFor, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, effect, inject } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatMenu, MatMenuContent, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatToolbar } from '@angular/material/toolbar';

import { GeoNetworkTool } from '@models/general/GeoNetworkTool';

import { AboutDialogComponent } from './about-dialog.component';
import { HeaderFacade } from './header.facade';
import { HeaderTool } from './header.model';
import { GeonetworkToolService } from './header.service';
import { AuthFacade } from '../../core/auth/auth.facade';

export const COVARIATE_TAB = 'covariates';
export const DISEASES_TAB = 'diseases';

const DefaultCategories: GeonetworkToolCategories[] = [
  {
    label: 'Text Mining',
    tools: [],
  },
  {
    label: 'Normalisation',
    tools: [],
  },
  {
    label: 'Risk Mapping',
    tools: [],
  },
  {
    label: 'Other Tools',
    tools: [],
  },
];

interface GeonetworkToolCategories {
  label: 'Text Mining' | 'Normalisation' | 'Risk Mapping' | 'Other Tools';
  tools: GeoNetworkTool[];
}

@Component({
  selector: 'mood-header',
  standalone: true,
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  imports: [MatToolbar, MatMenu, MatMenuItem, MatMenuTrigger, MatMenuContent, MatDivider, NgFor, NgIf, MatButton],
  providers: [GeonetworkToolService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnInit {
  private _authFacade = inject(AuthFacade);
  private _dialog = inject(MatDialog);
  public facade = inject(HeaderFacade);

  constructor(private _toolService: GeonetworkToolService) {
    effect(() => {
      if (this._authFacade.isAdmin()) {
        this.tools.push(this.adminTool);
      } else if (this.tools.includes(this.adminTool)) {
        this.tools = this.tools.filter((tool) => tool !== this.adminTool);
      }
    });
  }

  private readonly adminTool: HeaderTool = {
    label: 'Dataset admin',
    route: 'admin',
  };

  tools: HeaderTool[] = [
    {
      label: 'Covariates',
      route: 'core',
      params: {
        tab: COVARIATE_TAB,
      },
    },
    {
      label: 'Diseases',
      route: 'core',
      params: {
        tab: DISEASES_TAB,
      },
    },
    {
      label: 'GeoNetwork',
      route: 'https://mood-geonetwork.avia-gis.com/geonetwork/srv/eng/catalog.search',
      external: true,
    },
    // {
    //   label: "Epid Data Explorer",
    //   route: "tool/epid-data-explorer"
    // },
    {
      label: 'PADI-web',
      route: 'https://padi-web.cirad.fr/en/',
      external: true,
    },
  ];

  codeEntries: GeonetworkToolCategories[] = structuredClone(DefaultCategories);
  toolEntries: GeonetworkToolCategories[] = structuredClone(DefaultCategories);

  ngOnInit(): void {
    this.loadAndMapTools();
  }

  async loadAndMapTools() {
    const tools = await this._toolService.loadGeonetworkTools();

    tools.map((tool) => {
      switch (tool.type) {
        case 'service':
          this._checkToolToAdd(this.toolEntries, tool);
          break;

        case 'software':
          this._checkToolToAdd(this.codeEntries, tool);
          break;
      }
    });

    this.toolEntries = this.toolEntries.filter((category) => category.tools.length !== 0);
    this.toolEntries.forEach((category) => {
      category.tools.sort((toolA, toolB) => toolA.title.localeCompare(toolB.title));
    });

    this.codeEntries = this.codeEntries.filter((category) => category.tools.length !== 0);
    this.codeEntries.forEach((category) => {
      category.tools.sort((toolA, toolB) => toolA.title.localeCompare(toolB.title));
    });
  }

  private _checkToolToAdd(group: GeonetworkToolCategories[], tool: GeoNetworkTool) {
    let isMapped = false;
    if (tool.subject.includes('text-mining')) {
      group.find((category) => category.label === 'Text Mining')?.tools.push(tool);
      isMapped = true;
    }

    if (tool.subject.includes('risk mapping') || tool.subject.includes('simulation')) {
      group.find((category) => category.label === 'Risk Mapping')?.tools.push(tool);
      isMapped = true;
    }

    if (tool.subject.includes('data normalisation')) {
      group.find((category) => category.label === 'Normalisation')?.tools.push(tool);
      isMapped = true;
    }

    if (!isMapped) {
      group.find((category) => category.label === 'Other Tools')?.tools.push(tool);
    }
  }

  onGeonetworkToolClicked(tool: GeoNetworkTool) {
    this.facade.openGeonetworkTool(tool.identifier);
  }

  onToolClicked(tool: HeaderTool) {
    this.facade.openTool(tool);
  }

  openAboutDialog() {
    this._dialog.open(AboutDialogComponent, { maxWidth: '560px' });
  }
}
